
import { map, mergeMap } from 'rxjs/operators'

import { combineEpics } from 'redux-observable'
import { Epic } from 'interfaces/middleware'
import {
  GET_PUBLIC_TREND,
  getPublicTrendActionCreator,
  GET_PUBLIC_BREAKDOWN_BY_STATE,
  getPublicBreakdownByStateActionCreator,
  GET_PUBLIC_BREAKDOWN_BY_CATEGORY,
  getPublicBreakdownByCategoryActionCreator,
  GET_PUBLIC_BREAKDOWN_BY_MERCHANT,
  getPublicBreakdownByMerchantActionCreator,
} from 'actions/publicDashboardActions'
import { get } from 'helpers/observableAjaxHelpers'
import {
  GET_PUBLIC_TREND_URL,
  GET_PUBLIC_BREAKDOWN_BY_STATE_URL,
  GET_PUBLIC_BREAKDOWN_BY_CATEGORY_URL,
  GET_PUBLIC_BREAKDOWN_BY_MERCHANT_URL,
} from 'interfaces/ajax'
import { of } from 'rxjs'
import { normalize, getItemFromCollection } from 'helpers/normalizeHelpers'
import { Collection, IdValueEntity, Omit } from 'interfaces'
import {
  PublicBreakdownItem,
  PublicBreakdownByStateState,
  PublicBreakdownByCategoryState,
  PublicBreakdownByMerchantState,
  PublicFilter,
  PublicApiFilter,
  PublicBreakdownByStateApiPayload,
  PublicTrendApiPayload,
  PublicBreakdownByCategoryApiPayload,
  PublicBreakdownByMerchantApiPayload,
} from 'interfaces/publicDashboard'
import { PUBLIC_INCOME_RANGE_COLLECTION, PUBLIC_TIMEFRAME_COLLECTION } from 'constants/publicDashboard'
import { resolveCategory } from 'helpers/categoriesHelpers'
import { ALL_CATEGORY_ID } from 'constants/meta'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { catchAjaxSafe } from 'rxOperators/catchSafe'

const getPublicApiPayload = (filter: Partial<PublicFilter>): Partial<PublicApiFilter> => {
  const { category, incomeRange, location, timeframe } = filter

  const apiCategory = category ? resolveCategory(category, true) : null
  const apiLocation = location && location !== ALL_CATEGORY_ID
    ? location
    : null

  const incomeRangeItem = incomeRange && incomeRange !== ALL_CATEGORY_ID
    ? getItemFromCollection(PUBLIC_INCOME_RANGE_COLLECTION, incomeRange)
    : null

  const timeframeItem = timeframe
    ? getItemFromCollection(PUBLIC_TIMEFRAME_COLLECTION, timeframe)
    : null

  const apiPayload = {
    ...(apiCategory ? { category: apiCategory } : null),
    ...(apiLocation ? { state: apiLocation } : null),
    ...(incomeRangeItem ? { income_range: incomeRangeItem.serverValue } : null),
    ...(timeframeItem ? { months_limit: timeframeItem.serverValue } : null),
  }

  return apiPayload
}

const trendEpic: Epic = (action$, _store) => action$
  .pipe(filterRequestOfType(GET_PUBLIC_TREND, 'request')).pipe(
    mergeMap((action) => {
      const apiPayload = getPublicApiPayload(action.payload.params) as PublicTrendApiPayload

      return get(GET_PUBLIC_TREND_URL, apiPayload).pipe(
        map(({ response }) => getPublicTrendActionCreator.success(action.payload.params, response)))
        .pipe(catchAjaxSafe(() => of(getPublicTrendActionCreator.fail(action.payload.params))))
    }))

const normalizeBreakdown = <TCollection extends Collection<IdValueEntity<string, number>, 'id'>>(items: PublicBreakdownItem[], total?: number) => {
  const nextItems = items.map(({ label, value }) => ({ id: label, value }))
  const collection = normalize(nextItems, 'id') as TCollection

  return {
    items: collection,
    total,
  }
}

const breakdownByStateEpic: Epic = (action$, _store) => action$
  .pipe(filterRequestOfType(GET_PUBLIC_BREAKDOWN_BY_STATE, 'request')).pipe(
    mergeMap((action) => {
      const apiPayload = {
        ...getPublicApiPayload(action.payload.params) as Omit<PublicBreakdownByStateApiPayload, 'ordering'>,
        ordering: 'value' as const,
      }

      return get(GET_PUBLIC_BREAKDOWN_BY_STATE_URL, apiPayload).pipe(
        map(({ response }) => {
          const { items, total_change } = response
          const nextResponse = normalizeBreakdown(items, total_change) as PublicBreakdownByStateState

          return getPublicBreakdownByStateActionCreator.success(action.payload.params, nextResponse)
        }))
        .pipe(catchAjaxSafe(() => of(getPublicBreakdownByStateActionCreator.fail(action.payload.params))))
    }))

const breakdownByCategoryEpic: Epic = (action$, _store) => action$
  .pipe(filterRequestOfType(GET_PUBLIC_BREAKDOWN_BY_CATEGORY, 'request')).pipe(
    mergeMap((action) => {
      const apiPayload = {
        ...getPublicApiPayload(action.payload.params) as Omit<PublicBreakdownByCategoryApiPayload, 'ordering'>,
        ordering: 'value' as const,
      }

      return get(GET_PUBLIC_BREAKDOWN_BY_CATEGORY_URL, apiPayload).pipe(
        map(({ response }) => {
          const { items, total_change } = response
          const nextResponse = normalizeBreakdown(items, total_change) as PublicBreakdownByCategoryState

          return getPublicBreakdownByCategoryActionCreator.success(action.payload.params, nextResponse)
        }))
        .pipe(catchAjaxSafe(() => of(getPublicBreakdownByCategoryActionCreator.fail(action.payload.params))))
    }))

const breakdownByMerchantEpic: Epic = (action$, _store) => action$
  .pipe(filterRequestOfType(GET_PUBLIC_BREAKDOWN_BY_MERCHANT, 'request')).pipe(
    mergeMap((action) => {
      const apiPayload = {
        ...getPublicApiPayload(action.payload.params) as Omit<PublicBreakdownByMerchantApiPayload, 'ordering'>,
        ordering: '-total_spend' as const,
      }

      return get(GET_PUBLIC_BREAKDOWN_BY_MERCHANT_URL, apiPayload).pipe(
        map(({ response }) => {
          const { items } = response
          const nextResponse = normalizeBreakdown(items) as PublicBreakdownByMerchantState
          return getPublicBreakdownByMerchantActionCreator.success(action.payload.params, nextResponse)
        }))
        .pipe(catchAjaxSafe(() => of(getPublicBreakdownByMerchantActionCreator.fail(action.payload.params))))
    }))

export const publicDashboardEpic = combineEpics(
  trendEpic,
  breakdownByStateEpic,
  breakdownByCategoryEpic,
  breakdownByMerchantEpic,
)
